import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { IoIosArrowBack } from 'react-icons/io'
import { BsPlus } from 'react-icons/bs'
import { FiTrash2 } from 'react-icons/fi'
import * as authAction from '../store/authAction'
import * as postActions from '../data/posts_data'
import { addToPlaylist, createPlaylist, deletePlaylist, entryId, getPlaylists, playlistHas, removeFromPlaylist } from '../utils/playlists'
import { firstPostImage, uploadUrl } from '../utils/postImages'
import { isPhotoPost, mediaPath, openPostState } from '../utils/mediaRoute'
import '../styles/studio.css'

const num = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

const postTime = (post) => {
  const t = Date.parse(post?.date)
  return Number.isFinite(t) ? t : 0
}

const typeLabel = (type) => {
  if (type === 's') return 'Shorts'
  if (type === 'i') return 'Photos'
  if (type === 'm') return 'Movies'
  return 'Videos'
}

const BarChart = ({ items }) => {
  const max = Math.max(...items.map(item => item.value), 1)
  return (
    <div className="studioBars">
      {items.map(item => (
        <div key={item.label} className="studioBarRow">
          <span>{item.label}</span>
          <div className="studioBarTrack">
            <b style={{ width: `${Math.max(6, (item.value / max) * 100)}%` }} />
          </div>
          <em>{item.value}</em>
        </div>
      ))}
    </div>
  )
}

const ColumnChart = ({ items }) => {
  const max = Math.max(...items.map(item => item.value), 1)
  return (
    <div className="studioCols" role="img" aria-label="Views over the last 7 days">
      {items.map(item => (
        <div key={item.label} className="studioCol">
          <i style={{ height: `${Math.max(6, (item.value / max) * 100)}%` }} />
          <span>{item.label}</span>
          <em>{item.value}</em>
        </div>
      ))}
    </div>
  )
}

const Studio = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useSelector(state => state.authReduse)
  const posts = useSelector(state => state.PostReducer.userPosts || [])
  const [playlists, setPlaylists] = useState([])
  const [openId, setOpenId] = useState('')
  const [newName, setNewName] = useState('')
  const [adding, setAdding] = useState(false)
  const [fetchedPosts, setFetchedPosts] = useState([])
  const requestedIds = useRef(new Set())

  useEffect(() => {
    const boot = async () => {
      const userData = localStorage.getItem('userdata')
      if (!userData) {
        navigate('/auth', { replace: true })
        return
      }
      try {
        const { id, pid } = JSON.parse(userData)
        await dispatch(authAction.auth(id, pid))
        await dispatch(postActions.get_my_posts())
      } catch (err) {}
    }
    boot()
  }, [dispatch, navigate])

  useEffect(() => {
    if (user.id) setPlaylists(getPlaylists(user.id))
  }, [user.id])

  const stats = useMemo(() => {
    const views = posts.reduce((sum, post) => sum + num(post.views), 0)
    const likes = posts.reduce((sum, post) => sum + num(post.vlikes), 0)
    const comments = posts.reduce((sum, post) => sum + num(post.vcomments), 0)
    return { views, likes, comments, count: posts.length }
  }, [posts])

  const typeBars = useMemo(() => {
    const buckets = { l: 0, s: 0, i: 0, m: 0 }
    posts.forEach(post => {
      const key = buckets[post.type] == null ? 'l' : post.type
      buckets[key] += num(post.views)
    })
    return [
      { label: 'Videos', value: buckets.l },
      { label: 'Shorts', value: buckets.s },
      { label: 'Photos', value: buckets.i },
      { label: 'Movies', value: buckets.m }
    ]
  }, [posts])

  const topPosts = useMemo(() => {
    return [...posts]
      .sort((a, b) => num(b.views) - num(a.views))
      .slice(0, 6)
      .map(post => ({
        label: (post.title || 'Untitled').slice(0, 18),
        value: num(post.views)
      }))
  }, [posts])

  const weekCols = useMemo(() => {
    const days = []
    const now = new Date()
    for (let i = 6; i >= 0; i -= 1) {
      const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i)
      const key = day.toDateString()
      days.push({
        key,
        label: day.toLocaleDateString(undefined, { weekday: 'short' }).slice(0, 2),
        value: 0
      })
    }
    posts.forEach(post => {
      const t = postTime(post)
      if (!t) return
      const key = new Date(t).toDateString()
      const hit = days.find(item => item.key === key)
      if (hit) hit.value += num(post.views)
    })
    return days.map(({ label, value }) => ({ label, value }))
  }, [posts])

  const openList = playlists.find(item => item.id === openId)

  useEffect(() => {
    requestedIds.current = new Set()
    setFetchedPosts([])
  }, [openId])

  useEffect(() => {
    if (!openList) return
    const knownIds = new Set(posts.map(post => String(post.id)))
    const missing = (openList.postIds || [])
      .map(entry => {
        const id = entryId(entry)
        const snap = entry && typeof entry === 'object' ? entry : null
        if (!id || knownIds.has(id) || requestedIds.current.has(id)) return ''
        if (snap && (snap.thumbnail || snap.title || snap.image)) return ''
        return id
      })
      .filter(Boolean)
    if (!missing.length) return
    missing.forEach(id => requestedIds.current.add(id))
    let cancelled = false
    Promise.all(missing.map(async (id) => {
      try {
        const res = await fetch(`/api/postAi/postDeatils/${encodeURIComponent(id)}`)
        const data = await res.json()
        const row = Array.isArray(data.msg) ? data.msg[0] : data.msg
        if (!row || !row.id) return { id, title: 'Saved video', type: 'l' }
        return {
          id: row.id,
          title: row.title || '',
          thumbnail: row.thumbnail || '',
          type: row.type || 'l',
          image: row.image || row.content || ''
        }
      } catch (err) {
        return { id, title: 'Saved video', type: 'l' }
      }
    })).then(rows => {
      if (cancelled) return
      setFetchedPosts(prev => {
        const ids = new Set(prev.map(post => String(post.id)))
        return [...prev, ...rows.filter(row => row && !ids.has(String(row.id)))]
      })
    })
    return () => { cancelled = true }
  }, [openId, openList, posts])

  const openPosts = openList
    ? (openList.postIds || []).map(entry => {
        const id = entryId(entry)
        const live = posts.find(post => String(post.id) === id)
          || fetchedPosts.find(post => String(post.id) === id)
        if (live) return live
        if (entry && typeof entry === 'object') {
          return {
            id,
            title: entry.title || 'Saved video',
            thumbnail: entry.thumbnail || '',
            type: entry.type || 'l',
            image: entry.image || ''
          }
        }
        return id ? { id, title: 'Saved video', type: 'l' } : null
      }).filter(Boolean)
    : []

  const refresh = (list) => setPlaylists(list)

  const makeList = (event) => {
    event.preventDefault()
    if (!user.id || !newName.trim()) return
    refresh(createPlaylist(user.id, newName))
    setNewName('')
  }

  const goPost = (post) => {
    if (isPhotoPost(post)) {
      navigate(mediaPath(post), { state: openPostState(post, location, { feed: [], source: 'watch' }) })
      return
    }
    navigate(mediaPath(post), { state: { item: post } })
  }

  return (
    <section className="studioPage">
      <Helmet>
        <title>Studio</title>
        <meta name="theme-color" content="#000000" />
      </Helmet>
      <header className="studioTop">
        <button type="button" onClick={() => navigate('/profile')} aria-label="Back">
          <IoIosArrowBack />
        </button>
        <h1>Studio</h1>
      </header>

      <div className="studioStats">
        <article><strong>{stats.views}</strong><span>Views</span></article>
        <article><strong>{stats.likes}</strong><span>Likes</span></article>
        <article><strong>{stats.comments}</strong><span>Comments</span></article>
        <article><strong>{stats.count}</strong><span>Posts</span></article>
      </div>

      <section className="studioCard">
        <h2>Views this week</h2>
        <ColumnChart items={weekCols} />
      </section>

      <section className="studioCard">
        <h2>Views by type</h2>
        <BarChart items={typeBars} />
      </section>

      <section className="studioCard">
        <h2>Top posts</h2>
        {topPosts.length ? <BarChart items={topPosts} /> : <p className="studioEmpty">No posts yet</p>}
      </section>

      <section className="studioCard">
        <div className="studioCardHead">
          <h2>Playlists</h2>
        </div>
        <form className="studioCreate" onSubmit={makeList}>
          <input
            value={newName}
            onChange={event => setNewName(event.target.value)}
            placeholder="New playlist name"
            maxLength={40}
          />
          <button type="submit" aria-label="Create playlist"><BsPlus /></button>
        </form>
        {!playlists.length ? (
          <p className="studioEmpty">Make a playlist to group your videos</p>
        ) : (
          <div className="studioLists">
            {playlists.map(item => (
              <button
                key={item.id}
                type="button"
                className={`studioListBtn${openId === item.id ? ' isOn' : ''}`}
                onClick={() => { setOpenId(item.id === openId ? '' : item.id); setAdding(false) }}
              >
                <strong>{item.name}</strong>
                <span>{(item.postIds || []).length} items</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {openList ? (
        <section className="studioCard">
          <div className="studioCardHead">
            <h2>{openList.name}</h2>
            <button
              type="button"
              className="studioGhost"
              onClick={() => { refresh(deletePlaylist(user.id, openList.id)); setOpenId('') }}
              aria-label="Delete playlist"
            >
              <FiTrash2 />
            </button>
          </div>
          {openPosts.length ? (
            <div className="studioGrid">
              {openPosts.map(post => {
                const thumb = uploadUrl(post.thumbnail || firstPostImage(post.image))
                return (
                  <div key={post.id} className="studioThumb">
                    <button type="button" className="studioThumbMain" onClick={() => goPost(post)}>
                      {thumb ? <img src={thumb} alt="" /> : <div className="studioThumbBlank" />}
                      <span>{post.title || typeLabel(post.type)}</span>
                    </button>
                    <button
                      type="button"
                      className="studioThumbRemove"
                      onClick={() => refresh(removeFromPlaylist(user.id, openList.id, post.id))}
                    >
                      Remove
                    </button>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="studioEmpty">No videos in this playlist yet. Save one from the home feed, or add from your posts.</p>
          )}
          <button type="button" className="studioAdd" onClick={() => setAdding(prev => !prev)}>
            {adding ? 'Close library' : 'Add from your posts'}
          </button>
          {adding ? (
            <div className="studioGrid">
              {posts.filter(post => !playlistHas(openList, post.id)).map(post => (
                <button
                  key={post.id}
                  type="button"
                  className="studioThumb"
                  onClick={() => refresh(addToPlaylist(user.id, openList.id, post))}
                >
                  <img src={uploadUrl(post.thumbnail || firstPostImage(post.image))} alt="" />
                  <span>{post.title || typeLabel(post.type)}</span>
                </button>
              ))}
            </div>
          ) : null}
        </section>
      ) : null}
    </section>
  )
}

export default Studio
